"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import SectionContainer from "@/components/common/SectionContainer";
export default function LakesideEleganceAwaits() {
  const {
    ref: revealRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const sectionRef = useRef<HTMLElement | null>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const height = rect.height || 1;
      const progress = Math.min(Math.max(-rect.top / height, 0), 1);
      setScrollProgress(progress);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const bubbles = [{
    left: "8%",
    size: 4,
    duration: 14,
    delay: 0,
    drift: 12
  }, {
    left: "14%",
    size: 6,
    duration: 18,
    delay: 2,
    drift: -10
  }, {
    left: "22%",
    size: 3,
    duration: 12,
    delay: 4,
    drift: 8
  }, {
    left: "31%",
    size: 5,
    duration: 20,
    delay: 1,
    drift: -14
  }, {
    left: "42%",
    size: 4,
    duration: 16,
    delay: 3,
    drift: 10
  }, {
    left: "55%",
    size: 7,
    duration: 22,
    delay: 0,
    drift: -8
  }, {
    left: "63%",
    size: 3,
    duration: 13,
    delay: 5,
    drift: 14
  }, {
    left: "71%",
    size: 5,
    duration: 19,
    delay: 2,
    drift: -12
  }, {
    left: "79%",
    size: 4,
    duration: 15,
    delay: 4,
    drift: 9
  }, {
    left: "87%",
    size: 6,
    duration: 21,
    delay: 1,
    drift: -11
  }, {
    left: "93%",
    size: 3,
    duration: 17,
    delay: 3,
    drift: 7
  }];
  const parallaxY = scrollProgress * 80;
  const heroOpacity = Math.max(1 - scrollProgress * 1.6, 0);
  return <section data-section-id="4894" id="lakeside-elegance-awaits" ref={sectionRef} className="bg-background text-foreground relative h-[85vh] min-h-[560px] max-h-[820px] w-full overflow-hidden">
      <style>{`
        @keyframes shift-bubble-rise {
          0% {
            transform: translate3d(0, 110vh, 0) scale(0.6);
            opacity: 0;
          }
          12% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.5;
          }
          100% {
            transform: translate3d(var(--bx, 0px), -10vh, 0) scale(1.1);
            opacity: 0;
          }
        }
        @keyframes shift-scroll-pulse {
          0%, 100% {
            transform: scaleY(0.4);
            opacity: 0.3;
            transform-origin: top;
          }
          50% {
            transform: scaleY(1);
            opacity: 0.9;
            transform-origin: top;
          }
        }
        @keyframes shift-grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-3%, -2%); }
          20% { transform: translate(2%, 3%); }
          30% { transform: translate(-2%, 2%); }
          40% { transform: translate(3%, -3%); }
          50% { transform: translate(-3%, 3%); }
          60% { transform: translate(2%, -2%); }
          70% { transform: translate(-2%, -3%); }
          80% { transform: translate(3%, 2%); }
          90% { transform: translate(-3%, -2%); }
        }
      `}</style>

      {/* Background Image & Lightened Overlays */}
      <div className="absolute inset-0 will-change-transform" style={{
      transform: `translate3d(0, ${parallaxY}px, 0) scale(1.05)`,
      opacity: heroOpacity
    }}>
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/Main_Hero_Section.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/Main_Hero_Section.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1715425/img/Main_Hero_Section.jpg-wvc-sizes" alt="Serene lakeside view at Paradise Cove event venue" loading="eager" decoding="async" className="absolute inset-0 h-full w-full object-cover" style={{
        filter: "brightness(1.12) saturate(1.1) contrast(1.0)"
      }} />
        {/* Deepened radial gradient vignette overlay */}
        <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse at center, hsla(0,0%,0%,0) 40%, hsla(0,0%,0%,0.05) 75%, hsla(0,0%,0%,0.10) 100%)"
      }} />
        {/* Bottom-anchored gradient overlay to protect text contrast */}
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to top, hsla(0,0%,0%,0.30) 0%, hsla(0,0%,0%,0.12) 30%, hsla(0,0%,0%,0) 55%)"
      }} />
      </div>

      {/* Subtle Grain Overlay */}
      <div aria-hidden="true" className="pointer-events-none absolute -inset-[50%] opacity-[0.05] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
      animation: "shift-grain 1.2s steps(6) infinite"
    }} />

      {/* Ambient Rising Bubbles */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        {bubbles.map((b, i) => <span key={i} data-index={i} className="absolute bottom-0 block rounded-lg" style={{
        left: b.left,
        width: `${b.size}px`,
        height: `${b.size}px`,
        backgroundColor: "var(--primary)",
        boxShadow: "0 0 12px hsla(0, 0%, 0%, 0.35)",
        opacity: 0.5,
        animation: `shift-bubble-rise ${b.duration}s linear ${b.delay}s infinite`,
        ["--bx" as never]: `${b.drift}px`
      } as React.CSSProperties} />)}
      </div>

      {/* Soft feathered dark overlay behind the text block */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[5]" style={{
      opacity: heroOpacity,
      background: "radial-gradient(ellipse 90% 80% at 20% 90%, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.14) 40%, rgba(0,0,0,0) 75%), linear-gradient(to top, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.08) 40%, rgba(0,0,0,0) 70%)"
    }} />

      {/* Editorial Vertical Folio */}
      <div className="pointer-events-none absolute top-8 right-6 z-20 hidden md:block">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/80" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)",
        textShadow: "0 1px 6px rgba(0,0,0,0.6)"
      }}>
          Texas &middot; Lakeside Sanctuary
        </div>
      </div>

      {/* Main Content Layout */}
      <SectionContainer ref={revealRef} className="relative z-10 grid h-full grid-cols-12 items-end pb-24 md:pb-28 lg:pb-32" style={{
      opacity: heroOpacity
    }}>
        <div className={`col-span-12 sm:col-span-11 md:col-span-10 lg:col-span-9 transition-all duration-[1400ms] ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {/* Eyebrow Label */}
          <div className={`mb-8 flex items-center gap-3 font-mono font-semibold text-[11px] tracking-[0.28em] uppercase text-white/90 transition-all duration-1000 delay-100 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
          textShadow: "0 1px 3px rgba(0,0,0,0.7), 0 2px 12px rgba(0,0,0,0.6)"
        }}>
            <span className="inline-block h-px w-6 bg-white/50" aria-hidden="true" />
            <span>LAKEFRONT EVENT VENUE · WEDDINGS & EVENTS</span>
          </div>

          {/* Statement H1 */}
          <h1 className="font-default font-bold text-white" style={{
          fontSize: "clamp(2.5rem, 7vw, 6rem)",
          lineHeight: 0.92,
          letterSpacing: "-0.03em",
          textShadow: "0 2px 4px rgba(0,0,0,0.5), 0 4px 24px rgba(0,0,0,0.5)"
        }}>
            <span className="block transition-all duration-[1200ms] delay-200 ease-out translate-y-0 opacity-100">
              Lakeside
            </span>
            <span className="block transition-all duration-[1200ms] delay-[450ms] ease-out translate-y-0 opacity-100">
              Elegance{" "}
              <span className="italic font-bold" style={{
              color: "#ffffff"
            }}>
                Awaits
              </span>
            </span>
          </h1>

          {/* Body Intro */}
          <p className={`mt-8 max-w-xl font-default font-semibold text-base text-white sm:text-lg md:text-[1.125rem] transition-all duration-1000 delay-[700ms] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
          textShadow: "0 1px 3px rgba(0,0,0,0.75), 0 2px 16px rgba(0,0,0,0.7)"
        }}>
            Discover the perfect setting for your most cherished moments at our exclusive Texas waterfront venue.
          </p>

          {/* Anchor CTA */}
          <div className={`mt-10 transition-all duration-1000 delay-[900ms] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <Button asChild variant="outline" size="lg" className="group h-12 border border-white bg-white px-8 font-mono font-bold text-[13px] tracking-[0.24em] uppercase text-black hover:bg-white/90 hover:border-white/90 hover:text-black transition-colors duration-500 rounded-lg">
            <Link to="#contacthero">
              <span>Book Your Tour</span>
              <span aria-hidden="true" className="ml-2 inline-block transition-transform duration-500 group-hover:translate-x-1">
                &rarr;
              </span>
            </Link>
            </Button>
          </div>
        </div>
      </SectionContainer>

      {/* Floating Scroll Indicator */}
      <div className="pointer-events-none absolute bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-3" style={{
      opacity: heroOpacity
    }}>
        <span className="block h-12 w-px overflow-hidden bg-white/30">
          <span className="block h-full w-full bg-white/90" style={{
          animation: "shift-scroll-pulse 2.4s ease-in-out infinite"
        }} />
        </span>
        <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/70">
          Scroll
        </span>
      </div>
    </section>;
}